import { customerUpdateKinds, customerUpdateStates } from "@lafayette/domain";
import type { StaffFulfilmentViewResponse, StaffRequestEvidenceInput } from "@lafayette/schemas";
import { useRef, useState } from "react";

import { FieldGroup, FileField, SelectField, TextAreaField, TextField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import { RadioField } from "../../ui/radio";
import type { AnyRequestCommand, RequestCommandOutcome, RequestCommandResult } from "../commands";
import type { RequestFacts } from "../detail-model";
import { formatInstant } from "../format";
import {
  confirmationKindLabels,
  confirmationKindOptions,
  contactMethodOptions,
  evidenceKindOptions,
  fulfilmentKindLabels,
  updateKindLabels,
} from "../labels";
import { RequestCommandDialog } from "./CommandDialog";
import { hintClass } from "./parts";
import {
  emptyWallTime,
  resolveOptionalWallTime,
  type WallTimeError,
  WallTimeFields,
  type WallTimeState,
} from "./WallTimeFields";

// The dialogs of the fulfilment part (CHT-752): record evidence, confirm on a fresh confirmation reference, correct a confirmation,
// mark unavailable with customer-safe alternatives, publish a customer update, upload and share an attachment. Every time a staff
// member types is a wall time with an explicit IANA zone. Text that the CUSTOMER will read is labelled as such; text that stays inside
// is labelled "staff only".

type Evidence = StaffFulfilmentViewResponse["evidence"][number];
type Attachment = StaffFulfilmentViewResponse["attachments"][number];

type Common = Readonly<{
  facts: RequestFacts;
  run: (command: AnyRequestCommand) => Promise<RequestCommandResult>;
  onDone: (outcome: RequestCommandOutcome) => void;
  onClose: () => void;
}>;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const text = (value: string): string | undefined =>
  value.trim() === "" ? undefined : value.trim();
/** `{ key: value }` when there is a value, else nothing: a body never carries a key set to `undefined`. */
const opt = <K extends string>(key: K, value: string | undefined): Partial<Record<K, string>> =>
  value === undefined ? {} : ({ [key]: value } as Record<K, string>);

// --- record evidence (and correct one row) ----------------------------------------------------------------------------------------

export function RecordEvidenceDialog({
  facts,
  run,
  onDone,
  onClose,
  correctionOf,
}: Common & Readonly<{ correctionOf?: Evidence | undefined }>) {
  const correcting = correctionOf !== undefined;
  const [kind, setKind] = useState<string>("SUPPLIER_CONTACT");
  const [supplier, setSupplier] = useState("");
  const [method, setMethod] = useState("");
  const [said, setSaid] = useState<WallTimeState>(emptyWallTime(facts.timeZone));
  const [expires, setExpires] = useState<WallTimeState>(emptyWallTime(facts.timeZone));
  const [reference, setReference] = useState("");
  const [confirmationKind, setConfirmationKind] = useState("");
  const [quoteId, setQuoteId] = useState("");
  const [detail, setDetail] = useState("");
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  const [saidError, setSaidError] = useState<WallTimeError | undefined>();
  const [expiresError, setExpiresError] = useState<WallTimeError | undefined>();

  const effectiveKind = correcting ? "CORRECTION" : kind;
  const hasExpiry = effectiveKind === "QUOTE" || effectiveKind === "AVAILABILITY";

  return (
    <RequestCommandDialog
      title={correcting ? "Correct this evidence row" : "Record evidence"}
      description={
        correcting
          ? `The old ${fulfilmentKindLabels[correctionOf.kind]?.toLowerCase() ?? "row"} stays on the record, marked as corrected. Staff only.`
          : "Staff only. The customer never sees evidence. To tell the customer something, publish an update."
      }
      confirmLabel={correcting ? "Record the correction" : "Record evidence"}
      needsReason={false}
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        const found: Record<string, string> = {};
        if (effectiveKind === "SUPPLIER_CONTACT") {
          if (supplier.trim() === "") found.supplier = "Write the supplier.";
          if (method === "") found.method = "Choose how you were in touch.";
        }
        if (effectiveKind === "CONFIRMATION_REFERENCE") {
          if (reference.trim() === "")
            found.reference = "Write the reference code the supplier gave.";
          if (confirmationKind === "")
            found.confirmationKind = "Choose what kind of confirmation it is.";
        }
        if (effectiveKind === "QUOTE" && quoteId.trim() !== "" && !UUID.test(quoteId.trim())) {
          found.quoteId =
            "A quote id has 36 characters. Leave it empty if there is no approved quote yet.";
        }
        if (correcting && detail.trim() === "")
          found.detail = "Write what was wrong and what is right.";
        if (detail.trim().length > 2000) found.detail = "Use at most 2000 characters.";
        if (supplier.trim().length > 200) found.supplier = "Use at most 200 characters.";
        if (reference.trim().length > 200) found.reference = "Use at most 200 characters.";

        const saidResult = correcting
          ? { ok: true as const, instant: null }
          : resolveOptionalWallTime(said);
        const expiresResult = hasExpiry
          ? resolveOptionalWallTime(expires)
          : { ok: true as const, instant: null };
        setSaidError(saidResult.ok ? undefined : saidResult.error);
        setExpiresError(expiresResult.ok ? undefined : expiresResult.error);
        setErrors(found);
        if (Object.keys(found).length > 0 || !saidResult.ok || !expiresResult.ok)
          return { ok: false };

        const body: StaffRequestEvidenceInput = {
          kind: effectiveKind as StaffRequestEvidenceInput["kind"],
          ...(correcting ? { supersedesEvidenceId: correctionOf.id } : {}),
          ...(correcting ? {} : opt("supplierLabel", text(supplier))),
          ...(correcting || method === "" ? {} : { contactMethod: method as "PHONE" }),
          ...(saidResult.instant === null ? {} : { sourceTimestamp: saidResult.instant }),
          ...(effectiveKind === "CONFIRMATION_REFERENCE"
            ? {
                referenceCode: reference.trim(),
                confirmationKind: confirmationKind as "SUPPLIER_WRITTEN_CONFIRMATION",
              }
            : {}),
          ...(expiresResult.instant === null ? {} : { expiresAt: expiresResult.instant }),
          ...(effectiveKind === "QUOTE" && text(quoteId) !== undefined
            ? { paymentQuoteId: quoteId.trim().toLowerCase() }
            : {}),
          ...opt("detail", text(detail)),
        };
        return {
          ok: true,
          command: () => ({ command: "recordEvidence", requestId: facts.id, body }),
        };
      }}
    >
      {correcting ? null : (
        <SelectField
          id="request-evidence-kind"
          label="Kind of evidence"
          value={kind}
          onChange={setKind}
          options={evidenceKindOptions}
          required
        />
      )}
      {correcting ? null : (
        <>
          <TextField
            id="request-evidence-supplier"
            label="Supplier (staff only)"
            value={supplier}
            onChange={setSupplier}
            maxLength={200}
            required={effectiveKind === "SUPPLIER_CONTACT"}
            error={errors.supplier}
          />
          <SelectField
            id="request-evidence-method"
            label="How you were in touch"
            value={method}
            onChange={setMethod}
            options={contactMethodOptions}
            placeholder="Choose"
            required={effectiveKind === "SUPPLIER_CONTACT"}
            error={errors.method}
          />
          <WallTimeFields
            idPrefix="request-evidence-said"
            legend="When the supplier said it"
            hint="Optional. A wall time and its zone. It cannot be in the future."
            state={said}
            onChange={setSaid}
            error={saidError}
          />
        </>
      )}
      {effectiveKind === "CONFIRMATION_REFERENCE" ? (
        <>
          <TextField
            id="request-evidence-reference"
            label="Reference code"
            hint="The booking or confirmation reference the supplier gave. Staff only."
            value={reference}
            onChange={setReference}
            maxLength={200}
            required
            error={errors.reference}
          />
          <SelectField
            id="request-evidence-confirmation-kind"
            label="What kind of confirmation"
            value={confirmationKind}
            onChange={setConfirmationKind}
            options={confirmationKindOptions}
            placeholder="Choose"
            required
            error={errors.confirmationKind}
          />
        </>
      ) : null}
      {hasExpiry ? (
        <WallTimeFields
          idPrefix="request-evidence-expires"
          legend="Expires"
          hint="Optional. When the quote or the hold ends. It must be in the future."
          state={expires}
          onChange={setExpires}
          error={expiresError}
        />
      ) : null}
      {effectiveKind === "QUOTE" ? (
        <TextField
          id="request-evidence-quote"
          label="Approved payment quote (optional)"
          hint="The id of an existing approved quote of this request. No amount is entered or stored here: a payment amount comes from the approved quote only."
          value={quoteId}
          onChange={setQuoteId}
          maxLength={36}
          error={errors.quoteId}
        />
      ) : null}
      <TextAreaField
        id="request-evidence-detail"
        label={correcting ? "What was wrong and what is right (staff only)" : "Detail (staff only)"}
        value={detail}
        rows={4}
        maxLength={2000}
        required={correcting}
        error={errors.detail}
        onChange={setDetail}
      />
    </RequestCommandDialog>
  );
}

// --- confirm ------------------------------------------------------------------------------------------------------------------------

const NEW_ROW = "__new";

export function ConfirmDialog({
  facts,
  evidence,
  zone,
  run,
  onDone,
  onClose,
}: Common & Readonly<{ evidence: readonly Evidence[]; zone: string | null }>) {
  const fresh = evidence.filter((row) => row.canConfirm);
  const [choice, setChoice] = useState<string>(
    fresh.length > 0 ? (fresh[fresh.length - 1]?.id ?? NEW_ROW) : NEW_ROW,
  );
  const [reference, setReference] = useState("");
  const [confirmationKind, setConfirmationKind] = useState("");
  const [supplier, setSupplier] = useState("");
  const [method, setMethod] = useState("");
  const [said, setSaid] = useState<WallTimeState>(emptyWallTime(zone));
  const [saidError, setSaidError] = useState<WallTimeError | undefined>();
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  const recording = choice === NEW_ROW;

  // Recording a new reference and confirming are two server commands. The reference is saved first, and its id is KEPT here (`savedRef`):
  // a retry after a failed confirmation resumes with the confirm call only, so it never records a second row. The confirm call keeps its
  // own idempotency key for the retries of this dialog (`commands.ts`), so a confirmation the server already applied is replayed as a
  // success. If the confirmation fails, the answer says the reference IS saved.
  const pending = useRef<StaffRequestEvidenceInput | null>(null);
  const savedRef = useRef<string | null>(null);
  const [saved, setSaved] = useState<Readonly<{ code: string }> | null>(null);
  const runConfirm = async (command: AnyRequestCommand): Promise<RequestCommandResult> => {
    if (command.command !== "confirm" || command.body.evidenceId !== NEW_ROW) return run(command);
    if (savedRef.current === null) {
      if (pending.current === null) return run(command);
      const recorded = await run({
        command: "recordEvidence",
        requestId: facts.id,
        body: pending.current,
      });
      if (!recorded.ok) return recorded;
      if (!("evidenceId" in recorded.outcome)) return recorded;
      savedRef.current = recorded.outcome.evidenceId;
      setSaved({ code: pending.current.referenceCode ?? "" });
    }
    const confirmed = await run({
      ...command,
      body: { ...command.body, evidenceId: savedRef.current },
    });
    if (confirmed.ok) return confirmed;
    return {
      ok: false,
      failure: {
        ...confirmed.failure,
        message: `The confirmation reference was saved, but the request was not confirmed. ${confirmed.failure.message} The saved reference appears in the evidence list: choose it to confirm.`,
      },
    };
  };

  return (
    <RequestCommandDialog
      title="Confirm this request"
      description="A request is confirmed on evidence only: a fresh confirmation reference that a person recorded. Choose one, or record it now. Nothing you type here is shown to the customer."
      confirmLabel="Confirm request"
      run={runConfirm}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        const found: Record<string, string> = {};
        let body: StaffRequestEvidenceInput | null = null;
        // The reference is already saved: the form is closed and only the confirmation is (re)sent.
        if (savedRef.current !== null) {
          return {
            ok: true,
            command: (reason) => ({
              command: "confirm",
              requestId: facts.id,
              body: { expectedVersion: facts.version, reason, evidenceId: NEW_ROW },
            }),
          };
        }
        if (recording) {
          if (reference.trim() === "")
            found.reference = "Write the reference code the supplier gave.";
          if (reference.trim().length > 200) found.reference = "Use at most 200 characters.";
          if (confirmationKind === "")
            found.confirmationKind = "Choose what kind of confirmation it is.";
          const resolved = resolveOptionalWallTime(said);
          setSaidError(resolved.ok ? undefined : resolved.error);
          if (!resolved.ok) return { ok: false };
          if (Object.keys(found).length === 0) {
            body = {
              kind: "CONFIRMATION_REFERENCE",
              referenceCode: reference.trim(),
              confirmationKind: confirmationKind as "SUPPLIER_WRITTEN_CONFIRMATION",
              ...opt("supplierLabel", text(supplier)),
              ...(method === "" ? {} : { contactMethod: method as "PHONE" }),
              ...(resolved.instant === null ? {} : { sourceTimestamp: resolved.instant }),
            };
          }
        }
        setErrors(found);
        if (Object.keys(found).length > 0) return { ok: false };
        pending.current = body;
        return {
          ok: true,
          command: (reason) => ({
            command: "confirm",
            requestId: facts.id,
            body: {
              expectedVersion: facts.version,
              reason,
              evidenceId: recording ? NEW_ROW : choice,
            },
          }),
        };
      }}
    >
      {saved !== null ? (
        <Notice tone="success" role="status">
          <p>
            The confirmation reference {saved.code} is saved. It appears in the evidence list.
            Retrying only sends the confirmation again: no second reference is recorded.
          </p>
        </Notice>
      ) : null}
      {saved !== null ? null : (
        <FieldGroup legend="Evidence to confirm on">
          {fresh.length === 0 ? (
            <p className={hintClass}>
              There is no fresh confirmation reference yet. A reference recorded before the request
              last returned to work, or one that was corrected, cannot confirm it. Record one now.
            </p>
          ) : null}
          {fresh.map((row) => (
            <RadioField
              key={row.id}
              id={`request-confirm-${row.id}`}
              name="request-confirm-source"
              checked={choice === row.id}
              onChange={() => setChoice(row.id)}
              label={
                <>
                  Reference {row.referenceCode ?? "—"} (
                  {row.confirmationKind === null
                    ? "confirmation"
                    : (confirmationKindLabels[row.confirmationKind] ?? row.confirmationKind)}
                  ), recorded {formatInstant(row.observedAt)}
                </>
              }
            />
          ))}
          <RadioField
            id="request-confirm-new"
            name="request-confirm-source"
            checked={recording}
            onChange={() => setChoice(NEW_ROW)}
            label="Record a new confirmation reference"
          />
        </FieldGroup>
      )}
      {recording && saved === null ? (
        <>
          <TextField
            id="request-confirm-reference"
            label="Reference code"
            hint="The booking or confirmation reference the supplier gave. Staff only."
            value={reference}
            onChange={setReference}
            maxLength={200}
            required
            error={errors.reference}
          />
          <SelectField
            id="request-confirm-kind"
            label="What kind of confirmation"
            value={confirmationKind}
            onChange={setConfirmationKind}
            options={confirmationKindOptions}
            placeholder="Choose"
            required
            error={errors.confirmationKind}
          />
          <TextField
            id="request-confirm-supplier"
            label="Supplier (optional, staff only)"
            value={supplier}
            onChange={setSupplier}
            maxLength={200}
          />
          <SelectField
            id="request-confirm-method"
            label="How you were in touch (optional)"
            value={method}
            onChange={setMethod}
            options={contactMethodOptions}
            placeholder="Choose"
          />
          <WallTimeFields
            idPrefix="request-confirm-said"
            legend="When the supplier said it"
            hint="Optional. A wall time and its zone."
            state={said}
            onChange={setSaid}
            error={saidError}
          />
        </>
      ) : null}
    </RequestCommandDialog>
  );
}

// --- correct a confirmation ---------------------------------------------------------------------------------------------------------

export function CorrectConfirmationDialog({ facts, run, onDone, onClose }: Common) {
  const [detail, setDetail] = useState("");
  const [customerText, setCustomerText] = useState("");
  const [kind, setKind] = useState("CONFIRMATION_DETAILS");
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  return (
    <RequestCommandDialog
      title="Correct the confirmation"
      description="Nothing is edited or deleted. A correction row is added for staff, and the customer receives an update marked as a correction. The request stays confirmed."
      confirmLabel="Correct the confirmation"
      tone="danger"
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        const found: Record<string, string> = {};
        if (detail.trim() === "") found.detail = "Write what was wrong and what is right.";
        if (detail.trim().length > 2000) found.detail = "Use at most 2000 characters.";
        if (customerText.trim() === "") found.customerText = "Write what the customer should read.";
        if (customerText.trim().length > 1000) found.customerText = "Use at most 1000 characters.";
        setErrors(found);
        if (Object.keys(found).length > 0) return { ok: false };
        return {
          ok: true,
          command: (reason) => ({
            command: "correctConfirmation",
            requestId: facts.id,
            body: {
              expectedVersion: facts.version,
              reason,
              detail: detail.trim(),
              customerText: customerText.trim(),
              customerUpdateKind: kind as "CONFIRMATION_DETAILS",
            },
          }),
        };
      }}
    >
      <TextAreaField
        id="request-correction-detail"
        label="What was wrong and what is right (staff only)"
        value={detail}
        rows={3}
        maxLength={2000}
        required
        error={errors.detail}
        onChange={setDetail}
      />
      <TextAreaField
        id="request-correction-customer"
        label="What the customer reads"
        hint="The customer sees this text, marked as a correction. Write only what they may read."
        value={customerText}
        rows={3}
        maxLength={1000}
        required
        error={errors.customerText}
        onChange={setCustomerText}
      />
      <SelectField
        id="request-correction-kind"
        label="Kind of update"
        value={kind}
        onChange={setKind}
        options={["CONFIRMATION_DETAILS", "NEXT_STEP", "RESTRICTIONS"].map((value) => ({
          value,
          label: updateKindLabels[value] ?? value,
        }))}
        required
      />
    </RequestCommandDialog>
  );
}

// --- unavailable, with alternatives for the customer --------------------------------------------------------------------------------

export function UnavailableDialog({ facts, run, onDone, onClose }: Common) {
  const [detail, setDetail] = useState("");
  const [supplier, setSupplier] = useState("");
  const [method, setMethod] = useState("");
  const [note, setNote] = useState("");
  const [alternatives, setAlternatives] = useState("");
  const [line, setLine] = useState("");
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  return (
    <RequestCommandDialog
      title="Mark this request as unavailable?"
      description="Use this when the supplier cannot provide it. The request is closed as unavailable. What you write under 'The customer reads' is shown to the customer; everything else stays with staff."
      confirmLabel="Mark unavailable"
      tone="danger"
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        const found: Record<string, string> = {};
        if (detail.trim() === "") found.detail = "Write why it is unavailable.";
        if (detail.trim().length > 2000) found.detail = "Use at most 2000 characters.";
        if (note.trim().length > 1000) found.note = "Use at most 1000 characters.";
        if (alternatives.trim().length > 1000) found.alternatives = "Use at most 1000 characters.";
        if (line.trim().length > 500) found.line = "Use at most 500 characters.";
        setErrors(found);
        if (Object.keys(found).length > 0) return { ok: false };
        return {
          ok: true,
          command: (reason) => ({
            command: "unavailableWithAlternatives",
            requestId: facts.id,
            body: {
              expectedVersion: facts.version,
              reason,
              detail: detail.trim(),
              ...opt("supplierLabel", text(supplier)),
              ...(method === "" ? {} : { contactMethod: method as "PHONE" }),
              ...opt("customerNote", text(note)),
              ...opt("alternatives", text(alternatives)),
              ...opt("customerStatusSummary", text(line)),
            },
          }),
        };
      }}
    >
      <TextAreaField
        id="request-unavailable-detail"
        label="Why it is unavailable (staff only)"
        value={detail}
        rows={3}
        maxLength={2000}
        required
        error={errors.detail}
        onChange={setDetail}
      />
      <TextField
        id="request-unavailable-supplier"
        label="Supplier (optional, staff only)"
        value={supplier}
        onChange={setSupplier}
        maxLength={200}
      />
      <SelectField
        id="request-unavailable-method"
        label="How you were in touch (optional)"
        value={method}
        onChange={setMethod}
        options={contactMethodOptions}
        placeholder="Choose"
      />
      <TextAreaField
        id="request-unavailable-note"
        label="The customer reads: why it was not available (optional)"
        hint="Shown to the customer. No supplier names, contacts or internal detail."
        value={note}
        rows={2}
        maxLength={1000}
        error={errors.note}
        onChange={setNote}
      />
      <TextAreaField
        id="request-unavailable-alternatives"
        label="The customer reads: alternatives (optional)"
        hint="Shown to the customer as options they may choose instead."
        value={alternatives}
        rows={3}
        maxLength={1000}
        error={errors.alternatives}
        onChange={setAlternatives}
      />
      <TextAreaField
        id="request-unavailable-line"
        label="The customer reads: status line (optional)"
        value={line}
        rows={2}
        maxLength={500}
        error={errors.line}
        onChange={setLine}
      />
    </RequestCommandDialog>
  );
}

// --- publish a customer update ------------------------------------------------------------------------------------------------------

export function PublishUpdateDialog({
  facts,
  evidence,
  run,
  onDone,
  onClose,
}: Common & Readonly<{ evidence: readonly Evidence[] }>) {
  // The kinds of update that fit the state: the domain's own list (`customerUpdateStates`).
  const kinds = customerUpdateKinds.filter((kind) =>
    customerUpdateStates[kind].some((state) => state === facts.state),
  );
  const [kind, setKind] = useState(kinds[0] ?? "");
  const [body, setBody] = useState("");
  const [evidenceId, setEvidenceId] = useState("");
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  return (
    <RequestCommandDialog
      title="Publish an update for the customer"
      description="The customer reads this text exactly as written, and it cannot be edited or deleted afterwards. Publishing is the approval."
      confirmLabel="Publish to the customer"
      needsReason={false}
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        const found: Record<string, string> = {};
        if (kind === "") found.kind = "No update fits the state of this request.";
        if (body.trim() === "") found.body = "Write the update.";
        if (body.trim().length > 1000) found.body = "Use at most 1000 characters.";
        setErrors(found);
        if (Object.keys(found).length > 0) return { ok: false };
        return {
          ok: true,
          command: () => ({
            command: "publishUpdate",
            requestId: facts.id,
            body: {
              kind: kind as "NEXT_STEP",
              text: body.trim(),
              ...(evidenceId === "" ? {} : { evidenceId }),
            },
          }),
        };
      }}
    >
      <SelectField
        id="request-update-kind"
        label="Kind of update"
        hint="Only the kinds that fit the current state are listed. Nothing says booked or confirmed unless the state says so."
        value={kind}
        onChange={setKind}
        options={kinds.map((value) => ({ value, label: updateKindLabels[value] ?? value }))}
        required
        error={errors.kind}
      />
      <TextAreaField
        id="request-update-text"
        label="What the customer reads"
        value={body}
        rows={4}
        maxLength={1000}
        required
        error={errors.body}
        onChange={setBody}
      />
      {evidence.length === 0 ? null : (
        <SelectField
          id="request-update-evidence"
          label="Rests on this evidence (optional, staff only)"
          hint="A staff reference. The customer never sees it."
          value={evidenceId}
          onChange={setEvidenceId}
          options={evidence.map((row) => ({
            value: row.id,
            label: `${fulfilmentKindLabels[row.kind] ?? row.kind}, ${formatInstant(row.observedAt)}`,
          }))}
          placeholder="None"
        />
      )}
    </RequestCommandDialog>
  );
}

// --- attachments --------------------------------------------------------------------------------------------------------------------

const MAX_BYTES = 10 * 1024 * 1024;
const ATTACHMENT_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"] as const;
type AttachmentType = (typeof ATTACHMENT_TYPES)[number];
const isAttachmentType = (value: string): value is AttachmentType =>
  (ATTACHMENT_TYPES as readonly string[]).includes(value);

/**
 * Uploads one file to the request (CHT-754). The file goes straight to the private object store with a signed target, then the server
 * reads the stored bytes back and checks the size, the type and the first bytes before it writes the row. The page checks the same
 * things first so a wrong file is refused in words before it moves. A new file starts staff only.
 */
export function AttachmentDialog({ facts, run, onDone, onClose }: Common) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  return (
    <RequestCommandDialog
      title="Upload an attachment"
      description="Send a file for this request, for example a supplier quote or a confirmation screenshot. It starts as staff only: the customer cannot see it until you share it."
      confirmLabel="Upload"
      needsReason={false}
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        const found: Record<string, string> = {};
        if (file === null) found.file = "Choose a file.";
        else if (file.size < 1) found.file = "The file is empty. Choose another file.";
        else if (file.size > MAX_BYTES) found.file = "The file is larger than 10 MiB.";
        else if (!isAttachmentType(file.type)) {
          found.file = "Use a PDF, PNG, JPEG or WebP file.";
        }
        const label = name.trim() === "" ? (file?.name ?? "") : name.trim();
        if (label === "") found.name = "Write a name for the file.";
        if ([...label].length > 120) found.name = "Use at most 120 characters.";
        setErrors(found);
        if (Object.keys(found).length > 0 || file === null || !isAttachmentType(file.type)) {
          return { ok: false };
        }
        const upload = file;
        const contentType = file.type as AttachmentType;
        return {
          ok: true,
          command: () => ({
            command: "uploadAttachment",
            requestId: facts.id,
            body: { displayName: label, contentType, sizeBytes: upload.size },
            file: upload,
          }),
        };
      }}
    >
      <FileField
        id="request-attachment-file"
        label="File"
        hint="A PDF, PNG, JPEG or WebP file, from 1 byte to 10 MiB."
        accept={ATTACHMENT_TYPES.join(",")}
        required
        error={errors.file}
        onChange={(chosen) => {
          setFile(chosen);
          if (chosen !== null && name.trim() === "") setName(chosen.name.slice(0, 120));
        }}
      />
      <TextField
        id="request-attachment-name"
        label="Name of the file"
        hint="The name staff see in the list. The customer sees it only after you share the file."
        value={name}
        onChange={setName}
        maxLength={120}
        required
        error={errors.name}
      />
    </RequestCommandDialog>
  );
}

export function ShareAttachmentDialog({
  facts,
  attachment,
  run,
  onDone,
  onClose,
}: Common & Readonly<{ attachment: Attachment }>) {
  return (
    <RequestCommandDialog
      title="Share this file with the customer?"
      description="The customer will be able to open this file. They will see its name, type and size. This cannot be undone."
      confirmLabel="Share with the customer"
      tone="danger"
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => ({
        ok: true,
        command: (reason) => ({
          command: "shareAttachment",
          requestId: facts.id,
          attachmentId: attachment.id,
          body: { reason },
        }),
      })}
    >
      <p>
        <strong>{attachment.displayName}</strong>
      </p>
    </RequestCommandDialog>
  );
}
